import React from "react"
import { Button, Select, SelectItem, Slider, Switch } from "@heroui/react"
import { FlagIcon, LinkIcon, MagnifyingGlassCircleIcon } from "@heroicons/react/24/outline"
import { FlagIcon as SolidFlagIcon, UserIcon } from "@heroicons/react/20/solid"
import MainStore from "../MainStore"
import AxisPicker from "./AxisPicker"
import CollectionUtils from "../CollectionHelper"
import PrimitiveConfig from "../PrimitiveConfig"
import CategoryIdSelector from "./CategoryIdSelector"
import EditableResourceField from "../EditableResourceField"
import { DebouncedInput } from "./DebouncedInput"
import { DebouncedNumberInput } from "./DebouncedNumberInput"
import VerifiedUrl from "./VerifiedUrl"
import MarkdownEditor from "../MarkdownEditor"
import { roundCurrency } from "../SharedTransforms"

const coerceString = (value) => {
  if (value === null || value === undefined) return ""
  if (typeof value === "string") return value
  if (typeof value === "number" && Number.isFinite(value)) return `${value}`
  if (typeof value === "boolean") return value ? "true" : "false"
  return `${value}`
}

const normalizeOptions = (options = []) =>
  options.map((option) =>
    typeof option === "object" && option !== null
      ? { id: option.id ?? option.key ?? option.value ?? option.title, title: option.title ?? option.label ?? option.name ?? option.id }
      : { id: option, title: option }
  )

const isDefinedSelectionValue = (value) => value !== null && value !== undefined && value !== ""

const normalizeListValue = (value) => {
  if (Array.isArray(value)) return value
  if (typeof value === "string") {
    return value.split(",").map((item) => item.trim()).filter(Boolean)
  }
  return []
}

const normalizeOptionKeysForSelect = (value, multiple = false) => {
  const rawValues = multiple
    ? normalizeListValue(value ?? [])
    : [value].filter(isDefinedSelectionValue)
  return rawValues.map((entry) => coerceString(entry))
}

const normalizeOptionValueForWrite = (rawValue, options = []) => {
  const rawKey = coerceString(rawValue)
  const matched = options.find((option) => coerceString(option.id) === rawKey)
  return matched ? matched.id : rawValue
}

const normalizeOptionValuesForWrite = (rawValues, options = []) =>
  rawValues.map((entry) => normalizeOptionValueForWrite(entry, options))

const normalizeUrl = (value) => {
  if (typeof value !== "string") return undefined
  const trimmed = value.trim()
  if (!trimmed) return undefined
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`
}

const normalizeTypeList = (value) => {
  if (Array.isArray(value)) {
    return value.map((entry) => (typeof entry === "string" ? entry.trim() : entry)).filter(Boolean)
  }
  if (typeof value === "string") {
    return value.split(",").map((entry) => entry.trim()).filter(Boolean)
  }
  return value ? [value] : []
}

const mergeTypeFilters = (...sources) => {
  const normalizedSources = sources
    .map((source) => normalizeTypeList(source))
    .filter((source) => source.length > 0)

  if (normalizedSources.length === 0) {
    return undefined
  }

  let merged = Array.from(new Set(normalizedSources[0]))
  for (let i = 1; i < normalizedSources.length; i += 1) {
    const allowed = new Set(normalizedSources[i])
    merged = merged.filter((entry) => allowed.has(entry))
  }
  return merged
}

export default function FieldRenderer({
  field,
  value,
  primitive,
  items,
  editable = true,
  disabled = false,
  showLabel = true,
  density = "md",
  mode = "form",
  embedded = false,
  minor = false,
  callback,
  activeOnly,
  allowNone,
  local,
  primitiveList,
  selectionMode,
  ensurePresent,
  types,
  referenceIds,
  onChange,
}) {
  if (!field) return null

  const mainstore = MainStore()
  const isDisabled = disabled || !editable
  const fieldTitle = field.title ?? field.label ?? field.key ?? ""
  const label = showLabel ? fieldTitle : undefined
  const size = density === "xs" || density === "sm" ? "sm" : "md"
  const inputVariant = "bordered"
  const inputClassNames = {
    input: "text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500",
    inputWrapper: "dark:bg-slate-950 dark:border-slate-700",
  }
  const selectClassNames = {
    trigger: "text-slate-900 dark:text-slate-100",
    value: "text-slate-900 dark:text-slate-100",
    selectorIcon: "text-slate-400 dark:text-slate-500",
    popoverContent: "dark:bg-slate-950 dark:text-slate-100",
  }
  const updateValue = (next) => {
    if (callback) {
      return callback(next)
    }
    if (primitive && field.key) {
      return primitive.setParameter(field.key, next, undefined, field.forceUpdate)
    }
    return onChange?.(next)
  }

  const renderScale = () => {
    const minValue = field.minValue ?? 0
    const maxValue = field.maxValue ?? 9
    const step = field.stepValue ?? 1
    const current = value ?? minValue
    return (
      <Slider
        size={size}
        minValue={minValue}
        maxValue={maxValue}
        step={step}
        value={current}
        isDisabled={isDisabled}
        onChange={(next) => {
          const nextValue = Array.isArray(next) ? next[0] : next
          updateValue(nextValue)
        }}
        label={label}
      />
    )
  }

  const renderNumber = () => (
    <DebouncedNumberInput
      label={label}
      variant={inputVariant}
      value={value}
      minValue={0}
      isDisabled={isDisabled}
      classNames={inputClassNames}
      onChange={(next) => {
        if (typeof next === "number" && Number.isFinite(next)) {
          updateValue(next)
        }
      }}
    />
  )

  const renderList = () => {
    const isStringList = Array.isArray(value) && value.every((item) => typeof item === "string")
    const canEdit = isStringList && !isDisabled
    const displayValue = isStringList
      ? value.join(", ")
      : value != null
        ? JSON.stringify(value)
        : ""
    return (
      <DebouncedInput
        label={label}
        variant={inputVariant}
        value={displayValue}
        isReadOnly={!canEdit}
        classNames={inputClassNames}
        onChange={(next) => {
          if (canEdit) {
            updateValue(normalizeListValue(next))
          }
        }}
      />
    )
  }

  const renderers = {
    contact: () => null,
    contactName: () => null,
    markdown: () => (
      <MarkdownEditor
        initialMarkdown={value}
        onChange={(next) => updateValue(next)}
      />
    ),
    prompt: () => (
      <MarkdownEditor
        initialMarkdown={value}
        onChange={(next) => updateValue(next)}
      />
    ),
    verified_url: () => {
      if (minor) {
        const url = value?.trim()?.startsWith("http") ? value.trim() : `https://${value?.trim()}`
        return (
          <a className='flex space-x-2' href={url} target="_blank">
            <LinkIcon className='w-3' />
            <p className='break-all'>{value}</p>
          </a>
        )
      }
      return (
        <VerifiedUrl
          embedded={embedded || mode === "inline"}
          label={label}
          placeholder={field.placeholder}
          maxUrls={field.urlLimit ?? 1}
          className="w-full"
          value={value}
          onChange={(urls) => updateValue(Array.isArray(urls) ? urls.join(", ") : urls)}
        />
      )
    },
    boolean: () => {
      const selected = value !== undefined ? value : field.default
      return (
        <Switch
          size={size}
          isSelected={Boolean(selected)}
          isDisabled={isDisabled}
          onValueChange={(next) => updateValue(next)}
        >
          {label}
        </Switch>
      )
    },
    options: () => {
      const options = normalizeOptions(field.options)
      const multiple = !field.single
      const selected = normalizeOptionKeysForSelect(value ?? field.default, multiple)
      return (
        <Select
          label={label}
          size={size}
          variant={inputVariant}
          selectionMode={multiple ? "multiple" : "single"}
          selectedKeys={selected}
          disallowEmptySelection={!multiple}
          isDisabled={isDisabled}
          classNames={selectClassNames}
          onSelectionChange={(keys) => {
            const next = normalizeOptionValuesForWrite(Array.from(keys), options)
            updateValue(multiple ? next : next[0] ?? null)
          }}
        >
          {options.map((option) => (
            <SelectItem key={coerceString(option.id)}>{option.title}</SelectItem>
          ))}
        </Select>
      )
    },
    option_list: () => {
      const options = normalizeOptions(field.options)
      const multiple = Boolean(field.multi)
      const selected = normalizeOptionKeysForSelect(value ?? field.default, multiple)
      return (
        <Select
          label={label}
          size={size}
          variant={inputVariant}
          selectionMode={multiple ? "multiple" : "single"}
          selectedKeys={selected}
          disallowEmptySelection={!multiple}
          isDisabled={isDisabled}
          classNames={selectClassNames}
          onSelectionChange={(keys) => {
            const next = normalizeOptionValuesForWrite(Array.from(keys), options)
            updateValue(multiple ? next : next[0] ?? null)
          }}
        >
          {options.map((option) => (
            <SelectItem key={coerceString(option.id)}>{option.title}</SelectItem>
          ))}
        </Select>
      )
    },
    scale: () => renderScale(),
    progress: () => renderScale(),
    range: () => renderScale(),
    currency: () => {
      if (isDisabled) {
        return <div className="text-gray-500 font-medium">${roundCurrency(value ?? 0)}</div>
      }
      return (
        <DebouncedNumberInput
          label={label}
          variant={inputVariant}
          value={value}
          minValue={0}
          classNames={inputClassNames}
          onChange={(next) => updateValue(next)}
        />
      )
    },
    number: () => renderNumber(),
    integer: () => (
      <DebouncedNumberInput
        label={label}
        variant={inputVariant}
        value={value}
        minValue={0}
        isDisabled={isDisabled}
        classNames={inputClassNames}
        onChange={(next) => {
          if (typeof next === "number" && Number.isFinite(next)) {
            updateValue(Math.trunc(next))
          }
        }}
      />
    ),
    list: () => renderList(),
    object: () => renderList(),
    url: () => {
      const href = normalizeUrl(value)
      return (
        <div className="flex w-full items-center gap-2">
          <DebouncedInput
            label={label}
            variant={inputVariant}
            value={value}
            isDisabled={isDisabled}
            classNames={inputClassNames}
            className="w-full"
            onChange={(next) => updateValue(next)}
          />
          {href && (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
              title="Open link"
            >
              <LinkIcon className="h-4 w-4" />
            </a>
          )}
        </div>
      )
    },
    collection_axis: () => {
      const source = primitive?.findParentPrimitives({type: ["experiment", "activity"]})[0]
        ?? primitive?.findParentPrimitives({type: ["query", "view", "segment"]})[0]
      return (
        <AxisPicker
          source={source}
          value={value}
          onChange={async (next, idx) => {
            const path = idx === undefined ? field.key : `${field.key}.${idx}`
            await CollectionUtils.setPrimitiveAxis(primitive, next, path, false)
          }}
        />
      )
    },
    axis_parent: () => {
      if (!primitive) return null
      const axis = CollectionUtils.axisFromCollection(items ?? [primitive], primitive)
      const hasSegments = (items ?? [primitive]).some((d) => d.parentPrimitives.find((p) => p.type === "segment"))
      const options = [
        { id: null, title: "None" },
        hasSegments ? { id: "SEGMENT", title: "Segment parent" } : undefined,
        ...axis.filter((d) => d.category && d.type === "title").map((d) => ({
          id: d.relationship?.join("|"),
          title: d.category.title,
        })),
      ].filter(Boolean)
      const selected = primitive.referenceParameters?.[field.key]?.join("|")
      return (
        <Select
          label={label}
          size={size}
          variant={inputVariant}
          selectedKeys={[selected].filter(Boolean)}
          classNames={selectClassNames}
          onSelectionChange={(keys) => {
            const next = Array.from(keys)[0] ?? null
            if (primitive && field.key) {
              primitive.setParameter(field.key, next === null ? null : next.split("|"), false, true)
            }
          }}
        >
          {options.map((option) => (
            <SelectItem key={option.id}>{option.title}</SelectItem>
          ))}
        </Select>
      )
    },
    primitive: () => {
      const pickedItem = callback ? mainstore.primitive(value) : primitive?.primitives?.params?.[field.key].allItems?.[0]
      const clear = () => {
        const picked = primitive?.primitives?.params?.[field.key]
        if (picked) {
          primitive.removeRelationship(picked, `params.${field.key}`)
        }
      }
      const pick = () => {
        mainstore.globalPicker({
          root: undefined,
          callback: (picked) => {
            if (callback) {
              callback(picked.id)
            } else if (primitive) {
              primitive.addRelationship(picked, `params.${field.key}`)
            }
          },
          type: field.primitiveType,
          referenceId: field.referenceId,
        })
      }

      return (
        <div className="w-full flex items-center justify-between gap-2">
          <div className="text-sm text-slate-600 truncate">
            {pickedItem?.title ?? "None"}
          </div>
          <div className="flex gap-2">
            {pickedItem && (
              <Button size="sm" variant="light" onPress={clear}>
                Clear
              </Button>
            )}
            <Button
              size="sm"
              variant="flat"
              startContent={<MagnifyingGlassCircleIcon className="w-4 h-4" />}
              onPress={pick}
            >
              {pickedItem ? "Change" : "Select"}
            </Button>
          </div>
        </div>
      )
    },
    primitive_parent: () => {
      const pickedItem = primitive?.relationshipAtLevel(field.key, 1)?.[0]
      return <div className="w-full text-sm text-slate-600">{pickedItem?.title ?? "None"}</div>
    },
    workspace_flow_primitive: () => {
      if (!primitive) {
        return null
      }
      const workspaceId = primitive.workspaceId ?? mainstore.activeWorkspaceId
      const activeIds = new Set([primitive.id, primitive.configParent?.id].filter(Boolean).map((id) => `${id}`))
      const availableFlows = mainstore.primitives()
        .filter((entry) => {
          if (!entry || entry.type !== "flow") {
            return false
          }
          if (`${entry.workspaceId}` !== `${workspaceId}`) {
            return false
          }
          return !activeIds.has(`${entry.id}`)
        })
        .sort((a, b) => `${a?.title ?? ""}`.localeCompare(`${b?.title ?? ""}`))
      const existingTargets = primitive?.primitives?.validflow?.allItems ?? []
      const selectedTarget = existingTargets.find((entry) => entry && !activeIds.has(`${entry.id}`)) ?? null
      const selectedKey = selectedTarget ? `${selectedTarget.id}` : "none"

      return (
        <Select
          label={label}
          size={size}
          variant={inputVariant}
          selectedKeys={[selectedKey]}
          disallowEmptySelection={true}
          isDisabled={isDisabled}
          classNames={selectClassNames}
          onSelectionChange={(keys) => {
            const nextKey = `${Array.from(keys)[0] ?? "none"}`
            const existing = primitive?.primitives?.validflow?.allItems ?? []
            existing.forEach((entry) => {
              if (!entry || activeIds.has(`${entry.id}`)) {
                return
              }
              if (`${entry.id}` !== nextKey) {
                primitive.removeRelationship(entry, "validflow")
              }
            })
            if (nextKey === "none") {
              return
            }
            const alreadySelected = existing.some((entry) => `${entry?.id}` === nextKey)
            if (alreadySelected) {
              return
            }
            const nextTarget = mainstore.primitive(nextKey)
            if (nextTarget) {
              primitive.addRelationship(nextTarget, "validflow")
            }
          }}
        >
          <SelectItem key="none">None</SelectItem>
          {availableFlows.map((entry) => (
            <SelectItem key={`${entry.id}`}>{entry.title || `Flow #${entry.plainId ?? entry.id}`}</SelectItem>
          ))}
        </Select>
      )
    },
    generatorId: () => {
      const list = mainstore.categories().filter((d) => d.ai?.generate)
      return (
        <Select
          label={label}
          size={size}
          variant={inputVariant}
          selectedKeys={[value].flat().filter(Boolean)}
          onSelectionChange={(keys) => {
            const next = Array.from(keys)[0]
            updateValue(next)
          }}
        >
          {list.map((d, idx) => (
            <SelectItem key={idx}>{d.title}</SelectItem>
          ))}
        </Select>
      )
    },
    categoryId: () => {
      const isMulti = field.multi === true || selectionMode === "multiple" || Array.isArray(value ?? field.default)
      const categoryTypes = mergeTypeFilters(
        types,
        field?.primitiveTypes,
        field?.primitiveType,
        field?.allowedPrimitiveTypes,
        field?.allowedPrimitiveType,
        field?.categoryTypes,
        field?.categoryType,
        field?.types
      )
      const selectedValues = isMulti
        ? (Array.isArray(value) ? value : [value].filter((d) => d !== undefined && d !== null))
        : value
      return (
        <CategoryIdSelector
          title={field.title}
          activeOnly={activeOnly}
          allowNone={allowNone}
          item={field}
          local={local}
          primitive={primitive}
          primitiveList={primitiveList}
          selectedValues={selectedValues}
          selectionMode={isMulti ? "multiple" : selectionMode}
          types={categoryTypes}
          showCount
        />
      )
    },
    category_source: () => {
      let list = []
      if (allowNone) {
        list.push({ key: "none", title: "No items", categoryId: undefined, category: undefined })
      }
      let defaultConfig
      if (field.scope) {
        const origin = primitive?.origin
        if (origin) {
          let itemsForScope
          const unpackItems = (itemsToUnpack, level) => {
            const types = itemsToUnpack.map((d) => d.metadata).filter((d, i, a) => a.findIndex((d2) => d && d2 && d.id === d2.id) === i)
            for (const cat of types) {
              list.push({ key: "items", target: "items", title: cat.title, categoryId: cat.id, category: cat, pivot: level > 0 ? level : undefined })
            }
          }
          for (const scope of field.scope) {
            const scopeName = scope instanceof Object ? scope.scope : scope
            if (scopeName === "resultCategories") {
              for (const cat of origin.metadata?.resultCategories ?? []) {
                list.push({ key: `results.${cat.id}`, title: cat.title, categoryId: cat.resultCategoryId, category: cat })
              }
            } else if (scopeName === "items") {
              itemsForScope = itemsForScope || origin.itemsForProcessing
              unpackItems(itemsForScope)
            } else if (scopeName === "parents") {
              const typeFilter = scope?.types
              const seen = new Set()
              const walk = (itemsToWalk) => {
                let parents = itemsToWalk.flatMap((d) => d.parentPrimitives)
                parents = parents.filter((d) => {
                  if (seen.has(d.id)) return false
                  seen.add(d.id)
                  return true
                })
                return mainstore.uniquePrimitives(typeFilter ? parents.filter((d) => typeFilter.includes(d.type)) : parents)
              }
              let parents = walk(itemsForScope ?? [])
              let level = 1
              while (parents.length > 0) {
                unpackItems(parents, level)
                parents = walk(parents)
                level += 1
              }
            }
          }
        }
      } else {
        const task = primitive?.task
        if (!task?.metadata) {
          return null
        }
        defaultConfig = task.metadata.actions.find((d) => d.key === "categorize" || d.command === "categorize")
        const evidenceCategories = [task.metadata.evidenceCategories, task.primitives.descendants.filter((d) => d.type === "evidence").map((d) => d.referenceId)]
          .flat()
          .filter((c, idx, a) => c && a.indexOf(c) === idx)
        evidenceCategories.forEach((d) => {
          const cat = mainstore.category(d)
          const title = (field.excludeResultCategories ? "" : "Evidence: ") + cat.title
          if (!ensurePresent || task.primitives.descendants.filter((d2) => d2.referenceId === d).length > 0) {
            list.push({ key: d, isEvidence: true, target: cat.primitiveType ?? "evidence", title, categoryId: cat.id, category: cat })
          }
        })
        if (task?.metadata?.resultCategories) {
          task.metadata.resultCategories.forEach((d) => {
            const cat = mainstore.category(d.resultCategoryId)
            if (local) {
              list.push({ key: cat.id, title: cat.title, category: cat })
            } else {
              list.push({ key: `results.${d.id}`, title: d.title, categoryId: cat.id, category: cat })
            }
          })
        }
      }
      if (types) {
        list = list.filter((d) => !d.category || types.includes(d.category.primitiveType))
      }
      if (referenceIds) {
        list = list.filter((d) => referenceIds.includes(d.category?.id))
      }
      const index = list.findIndex((d) => d.key === value || d.categoryId === primitive?.referenceParameters?.referenceId)
      return (
        <Select
          label={label}
          size={size}
          variant={inputVariant}
          selectedKeys={[index].filter((d) => d !== -1)}
          onSelectionChange={(keys) => {
            const idx = Array.from(keys)[0]
            const source = list[idx]
            if (!source) return
            if (callback) {
              callback(parseInt(source.key))
              return
            }
            if (source.target) {
              primitive.setParameter("referenceId", source.categoryId)
              primitive.setParameter("pivot", source.pivot ?? 0, false, true)
              primitive.setParameter(field.key, source.target)
            } else {
              primitive.setParameter(field.key, source.key)
            }
            const fieldKey = Object.keys(primitive.metadata.parameters).find((d) => primitive.metadata.parameters[d].type === "category_field")
            if (fieldKey) {
              primitive.setParameter(fieldKey, "title")
            }
          }}
        >
          {list.map((d, idx) => (
            <SelectItem key={idx}>{d.title}</SelectItem>
          ))}
        </Select>
      )
    },
    state: () => {
      const stateInfo = PrimitiveConfig.stateInfo[primitive?.type] || PrimitiveConfig.stateInfo["default"]
      const options = Object.keys(stateInfo).map((key) => ({ id: key, ...stateInfo[key] }))
      if (!editable) {
        const state = stateInfo[primitive?.referenceParameters?.state ?? "open"]
        return (
          <div className={`text-xs bg-${state.colorBase}-100 h-fit whitespace-nowrap truncate rounded-full px-1.5 py-0.5 text-${state.colorBase}-800`}>
            {state.title}
          </div>
        )
      }
      return (
        <Select
          label={label}
          size={size}
          variant={inputVariant}
          selectedKeys={[primitive?.referenceParameters?.state ?? "open"]}
          onSelectionChange={(keys) => {
            const next = Array.from(keys)[0]
            primitive?.setParameter("state", next)
          }}
        >
          {options.map((option) => (
            <SelectItem key={option.id}>{option.title}</SelectItem>
          ))}
        </Select>
      )
    },
    phase: () => {
      if (primitive?.type !== "assessment") {
        return null
      }
      const framework = primitive.framework
      const options = Object.keys(framework.phases).map((key) => ({ id: parseInt(key, 10), ...framework.phases[key] }))
      return (
        <Select
          label={label}
          size={size}
          variant={inputVariant}
          selectedKeys={[parseInt(primitive.referenceParameters?.phase, 10)]}
          onSelectionChange={(keys) => {
            const next = Array.from(keys)[0]
            primitive.setParameter("phase", next)
          }}
        >
          {options.map((option) => (
            <SelectItem key={option.id}>{option.title}</SelectItem>
          ))}
        </Select>
      )
    },
    category_field: () => {
      const task = primitive?.task
      const defaultConfig = field.itemMeta ? undefined : task?.metadata?.actions.find((d) => d.key === "categorize" || d.command === "categorize")
      let sourceMeta
      const target = primitive?.referenceParameters?.target ? primitive.referenceParameters?.target : defaultConfig?.target
      const refId = primitive?.referenceParameters?.referenceId ?? defaultConfig?.referenceId
      if (field.localCategoryId) {
        sourceMeta = mainstore.category(field.localCategoryId)
      } else if (primitive?.referenceParameters?.referenceId) {
        sourceMeta = mainstore.category(primitive.referenceParameters?.referenceId)
      } else if (field.itemMeta) {
        let list = primitive?.itemsForProcessing
        if (list?.length === 0 && primitive?.flowElement) {
          list = primitive?.primitives?.config?.allItems?.flatMap((d) => d.itemsForProcessing[0])
        }
        if (list?.[0]) {
          sourceMeta = list[0].metadata
        }
      } else if (target === "evidence" || target === "items") {
        sourceMeta = mainstore.category(refId)
      } else if (target === "origin") {
        sourceMeta = primitive.origin
      } else if (task && target?.slice(0, 7) === "results") {
        sourceMeta = mainstore.category(task.metadata?.resultCategories[target.slice(8)].resultCategoryId)
      } else {
        sourceMeta = mainstore.category(refId)
      }
      const list = [{ key: "title", title: "Title" }]
      if (sourceMeta?.parameters) {
        Object.keys(sourceMeta.parameters).forEach((key) => {
          const param = sourceMeta.parameters[key]
          if ((param.type === "url" || param.type === "markdown" || param.type === "string" || param.type === "long_string" || param.type === "list") && !param.hidden) {
            list.push({ key: `param.${key}`, title: param.title })
          }
        })
      }
      if (field.includeContext) {
        list.push({ key: "context", title: "Context" })
      }
      if (field.includeContent) {
        list.push({ key: "full_content", title: "Content" })
      }
      return (
        <Select
          label={label}
          size={size}
          variant={inputVariant}
          selectedKeys={[value].filter(Boolean)}
          onSelectionChange={(keys) => {
            const next = Array.from(keys)[0]
            updateValue(next)
          }}
        >
          {list.map((option) => (
            <SelectItem key={option.key}>{option.title}</SelectItem>
          ))}
        </Select>
      )
    },
    flag: () => (
      <div
        className={`w-full relative justify-end flex ${editable ? "ring-2 ring-indigo-500 ring-offset-2" : ""}`}
        onClick={editable ? () => updateValue(value ? undefined : true) : undefined}
      >
        {value
          ? <SolidFlagIcon className={`w-5 h-5 ${`text-${field.color}-400` || "text-red-400"}`} />
          : <FlagIcon className={`w-5 h-5 ${`text-${field.color}-400` || "text-red-400"}`} />
        }
      </div>
    ),
    link: () => (
      <EditableResourceField
        onSelect={(next) => updateValue(next ? next : null)}
        value={value}
      />
    ),
    internal_parent_pin: () => {
      let options = []
      let current = ""
      const page = primitive?.origin
      if (page) {
        const pins = page.inputPins
        options = Object.keys(pins).map((d) => ({ id: d, title: pins[d].name })).filter((d) => d.id !== "impin")
        const outputs = page.primitives.outputs
        current = Object.keys(outputs).find((d) => outputs[d].includes(primitive.id))?.split("_")?.[0]
      }
      return (
        <Select
          label={label}
          size={size}
          variant={inputVariant}
          selectedKeys={[current].filter(Boolean)}
          onSelectionChange={(keys) => {
            const next = Array.from(keys)[0]
            const outputs = page.primitives.outputs
            const existing = Object.keys(outputs).find((d) => outputs[d].includes(primitive.id))
            const newPath = `${next}_impin`
            if (existing !== newPath) {
              page.removeRelationship(primitive, `outputs.${existing}`)
              page.addRelationship(primitive, `outputs.${newPath}`)
            }
          }}
        >
          {options.map((option) => (
            <SelectItem key={option.id}>{option.title}</SelectItem>
          ))}
        </Select>
      )
    },
    actionlist: () => {
      const actions = primitive?.referenceParameters?.referenceId ? mainstore.category(primitive?.referenceParameters?.referenceId).actions ?? [] : []
      return (
        <Select
          label={label}
          size={size}
          variant={inputVariant}
          selectedKeys={[value].filter(Boolean)}
          onSelectionChange={(keys) => {
            const next = Array.from(keys)[0]
            updateValue(next)
          }}
        >
          {actions.map((action) => (
            <SelectItem key={action.key}>{action?.title}</SelectItem>
          ))}
        </Select>
      )
    },
    user: () => {
      const user = mainstore.user(value)
      const name = user?.name || value || field.default || "Unassigned"
      const inlineClass = mode === "inline" ? "truncate" : ""
      return (
        <div className={`flex place-items-center ${inlineClass} text-gray-500 font-medium`}>
          <UserIcon className="w-4 h-4 mr-1 text-slate-300" />
          {name}
        </div>
      )
    },
  }

  if (renderers[field.type]) {
    return renderers[field.type]()
  }

  if (field.type === "collection_axis") {
    const source = primitive?.findParentPrimitives({type: ["experiment", "activity"]})[0]
      ?? primitive?.findParentPrimitives({type: ["query", "view", "segment"]})[0]
    return (
      <AxisPicker
        source={source}
        value={value}
        onChange={async (next, idx) => {
          const path = idx === undefined ? field.key : `${field.key}.${idx}`
          await CollectionUtils.setPrimitiveAxis(primitive, next, path, false)
        }}
      />
    )
  }

  if (field.type === "axis_parent") {
    if (!primitive) return null
    const axis = CollectionUtils.axisFromCollection(items ?? [primitive], primitive)
    const hasSegments = (items ?? [primitive]).some((d) => d.parentPrimitives.find((p) => p.type === "segment"))
    const options = [
      { id: null, title: "None" },
      hasSegments ? { id: "SEGMENT", title: "Segment parent" } : undefined,
      ...axis.filter((d) => d.category && d.type === "title").map((d) => ({
        id: d.relationship?.join("|"),
        title: d.category.title,
      })),
    ].filter(Boolean)
    const selected = primitive.referenceParameters?.[field.key]?.join("|")
    return (
      <Select
        label={label}
        size={size}
        variant={inputVariant}
        selectedKeys={[selected].filter(Boolean)}
        onSelectionChange={(keys) => {
          const next = Array.from(keys)[0] ?? null
          if (primitive && field.key) {
            primitive.setParameter(field.key, next === null ? null : next.split("|"), false, true)
          }
        }}
      >
        {options.map((option) => (
          <SelectItem key={option.id}>{option.title}</SelectItem>
        ))}
      </Select>
    )
  }

  if (field.type === "primitive") {
    const pickedItem = callback ? mainstore.primitive(value) : primitive?.primitives?.params?.[field.key].allItems?.[0]
    const clear = () => {
      const picked = primitive?.primitives?.params?.[field.key]
      if (picked) {
        primitive.removeRelationship(picked, `params.${field.key}`)
      }
    }
    const pick = () => {
      mainstore.globalPicker({
        root: undefined,
        callback: (picked) => {
          if (callback) {
            callback(picked.id)
          } else if (primitive) {
            primitive.addRelationship(picked, `params.${field.key}`)
          }
        },
        type: field.primitiveType,
        referenceId: field.referenceId,
      })
    }

    return (
      <div className="w-full flex items-center justify-between gap-2">
        <div className="text-sm text-slate-600 truncate">
          {pickedItem?.title ?? "None"}
        </div>
        <div className="flex gap-2">
          {pickedItem && (
            <Button size="sm" variant="light" onPress={clear}>
              Clear
            </Button>
          )}
          <Button
            size="sm"
            variant="flat"
            startContent={<MagnifyingGlassCircleIcon className="w-4 h-4" />}
            onPress={pick}
          >
            {pickedItem ? "Change" : "Select"}
          </Button>
        </div>
      </div>
    )
  }

  if (field.type === "primitive_parent") {
    const pickedItem = primitive?.relationshipAtLevel(field.key, 1)?.[0]
    return <div className="w-full text-sm text-slate-600">{pickedItem?.title ?? "None"}</div>
  }

  if (field.type === "generatorId") {
    const list = mainstore.categories().filter((d) => d.ai?.generate)
    return (
      <Select
        label={label}
        size={size}
        variant={inputVariant}
        selectedKeys={[value].flat().filter(Boolean)}
        onSelectionChange={(keys) => {
          const next = Array.from(keys)[0]
          updateValue(next)
        }}
      >
        {list.map((d, idx) => (
          <SelectItem key={idx}>{d.title}</SelectItem>
        ))}
      </Select>
    )
  }

  if (field.type === "categoryId") {
    const isMulti = field.multi === true || selectionMode === "multiple" || Array.isArray(value ?? field.default)
    const categoryTypes = mergeTypeFilters(
      types,
      field?.primitiveTypes,
      field?.primitiveType,
      field?.allowedPrimitiveTypes,
      field?.allowedPrimitiveType,
      field?.categoryTypes,
      field?.categoryType,
      field?.types
    )
    const selectedValues = isMulti
      ? (Array.isArray(value) ? value : [value].filter((d) => d !== undefined && d !== null))
      : value
    return (
      <CategoryIdSelector
        title={field.title}
        activeOnly={activeOnly}
        allowNone={allowNone}
        item={field}
        local={local}
        primitive={primitive}
        primitiveList={primitiveList}
        selectedValues={selectedValues}
        selectionMode={isMulti ? "multiple" : selectionMode}
        types={categoryTypes}
        showCount
      />
    )
  }

  if (field.type === "category_source") {
    let list = []

    if (allowNone) {
      list.push({ key: "none", title: "No items", categoryId: undefined, category: undefined })
    }

    let defaultConfig
    if (field.scope) {
      const origin = primitive?.origin
      if (origin) {
        let itemsForScope
        const unpackItems = (itemsToUnpack, level) => {
          const types = itemsToUnpack.map((d) => d.metadata).filter((d, i, a) => a.findIndex((d2) => d && d2 && d.id === d2.id) === i)
          for (const cat of types) {
            list.push({ key: "items", target: "items", title: cat.title, categoryId: cat.id, category: cat, pivot: level > 0 ? level : undefined })
          }
        }
        for (const scope of field.scope) {
          const scopeName = scope instanceof Object ? scope.scope : scope
          if (scopeName === "resultCategories") {
            for (const cat of origin.metadata?.resultCategories ?? []) {
              list.push({ key: `results.${cat.id}`, title: cat.title, categoryId: cat.resultCategoryId, category: cat })
            }
          } else if (scopeName === "items") {
            itemsForScope = itemsForScope || origin.itemsForProcessing
            unpackItems(itemsForScope)
          } else if (scopeName === "parents") {
            const typeFilter = scope?.types
            const seen = new Set()
            const walk = (itemsToWalk) => {
              let parents = itemsToWalk.flatMap((d) => d.parentPrimitives)
              parents = parents.filter((d) => {
                if (seen.has(d.id)) return false
                seen.add(d.id)
                return true
              })
              return mainstore.uniquePrimitives(typeFilter ? parents.filter((d) => typeFilter.includes(d.type)) : parents)
            }
            let parents = walk(itemsForScope ?? [])
            let level = 1
            while (parents.length > 0) {
              unpackItems(parents, level)
              parents = walk(parents)
              level += 1
            }
          }
        }
      }
    } else {
      const task = primitive?.task
      if (!task?.metadata) {
        return null
      }
      defaultConfig = task.metadata.actions.find((d) => d.key === "categorize" || d.command === "categorize")
      const evidenceCategories = [task.metadata.evidenceCategories, task.primitives.descendants.filter((d) => d.type === "evidence").map((d) => d.referenceId)]
        .flat()
        .filter((c, idx, a) => c && a.indexOf(c) === idx)

      evidenceCategories.forEach((d) => {
        const cat = mainstore.category(d)
        const title = (field.excludeResultCategories ? "" : "Evidence: ") + cat.title
        if (!ensurePresent || task.primitives.descendants.filter((d2) => d2.referenceId === d).length > 0) {
          list.push({ key: d, isEvidence: true, target: cat.primitiveType ?? "evidence", title, categoryId: cat.id, category: cat })
        }
      })

      if (task?.metadata?.resultCategories) {
        task.metadata.resultCategories.forEach((d) => {
          const cat = mainstore.category(d.resultCategoryId)
          if (local) {
            list.push({ key: cat.id, title: cat.title, category: cat })
          } else {
            list.push({ key: `results.${d.id}`, title: d.title, categoryId: cat.id, category: cat })
          }
        })
      }
    }

    if (types) {
      list = list.filter((d) => !d.category || types.includes(d.category.primitiveType))
    }

    if (referenceIds) {
      list = list.filter((d) => referenceIds.includes(d.category?.id))
    }

    const index = list.findIndex((d) => d.key === value || d.categoryId === primitive?.referenceParameters?.referenceId)
    return (
      <Select
        label={label}
        size={size}
        variant={inputVariant}
        selectedKeys={[index].filter((d) => d !== -1)}
        onSelectionChange={(keys) => {
          const idx = Array.from(keys)[0]
          const source = list[idx]
          if (!source) return
          if (callback) {
            callback(parseInt(source.key))
            return
          }
          if (source.target) {
            primitive.setParameter("referenceId", source.categoryId)
            primitive.setParameter("pivot", source.pivot ?? 0, false, true)
            primitive.setParameter(field.key, source.target)
          } else {
            primitive.setParameter(field.key, source.key)
          }
          const fieldKey = Object.keys(primitive.metadata.parameters).find((d) => primitive.metadata.parameters[d].type === "category_field")
          if (fieldKey) {
            primitive.setParameter(fieldKey, "title")
          }
        }}
      >
        {list.map((d, idx) => (
          <SelectItem key={idx}>{d.title}</SelectItem>
        ))}
      </Select>
    )
  }

  if (field.type === "state") {
    const stateInfo = PrimitiveConfig.stateInfo[primitive?.type] || PrimitiveConfig.stateInfo["default"]
    const options = Object.keys(stateInfo).map((key) => ({ id: key, ...stateInfo[key] }))
    if (!editable) {
      const state = stateInfo[primitive?.referenceParameters?.state ?? "open"]
      return (
        <div className={`text-xs bg-${state.colorBase}-100 h-fit whitespace-nowrap truncate rounded-full px-1.5 py-0.5 text-${state.colorBase}-800`}>
          {state.title}
        </div>
      )
    }
    return (
      <Select
        label={label}
        size={size}
        variant={inputVariant}
        selectedKeys={[primitive?.referenceParameters?.state ?? "open"]}
        onSelectionChange={(keys) => {
          const next = Array.from(keys)[0]
          primitive?.setParameter("state", next)
        }}
      >
        {options.map((option) => (
          <SelectItem key={option.id}>{option.title}</SelectItem>
        ))}
      </Select>
    )
  }

  if (field.type === "phase") {
    if (primitive?.type !== "assessment") {
      return null
    }
    const framework = primitive.framework
    const options = Object.keys(framework.phases).map((key) => ({ id: parseInt(key, 10), ...framework.phases[key] }))
    return (
      <Select
        label={label}
        size={size}
        variant={inputVariant}
        selectedKeys={[parseInt(primitive.referenceParameters?.phase, 10)]}
        onSelectionChange={(keys) => {
          const next = Array.from(keys)[0]
          primitive.setParameter("phase", next)
        }}
      >
        {options.map((option) => (
          <SelectItem key={option.id}>{option.title}</SelectItem>
        ))}
      </Select>
    )
  }

  if (field.type === "category_field") {
    const task = primitive?.task
    const defaultConfig = field.itemMeta ? undefined : task?.metadata?.actions.find((d) => d.key === "categorize" || d.command === "categorize")
    let sourceMeta
    const target = primitive?.referenceParameters?.target ? primitive.referenceParameters?.target : defaultConfig?.target
    const refId = primitive?.referenceParameters?.referenceId ?? defaultConfig?.referenceId

    if (field.localCategoryId) {
      sourceMeta = mainstore.category(field.localCategoryId)
    } else if (primitive?.referenceParameters?.referenceId) {
      sourceMeta = mainstore.category(primitive.referenceParameters?.referenceId)
    } else if (field.itemMeta) {
      let list = primitive?.itemsForProcessing
      if (list?.length === 0 && primitive?.flowElement) {
        list = primitive?.primitives?.config?.allItems?.flatMap((d) => d.itemsForProcessing[0])
      }
      if (list?.[0]) {
        sourceMeta = list[0].metadata
      }
    } else if (target === "evidence" || target === "items") {
      sourceMeta = mainstore.category(refId)
    } else if (target === "origin") {
      sourceMeta = primitive.origin
    } else if (task && target?.slice(0, 7) === "results") {
      sourceMeta = mainstore.category(task.metadata?.resultCategories[target.slice(8)].resultCategoryId)
    } else {
      sourceMeta = mainstore.category(refId)
    }

    const list = [{ key: "title", title: "Title" }]
    if (sourceMeta?.parameters) {
      Object.keys(sourceMeta.parameters).forEach((key) => {
        const param = sourceMeta.parameters[key]
        if ((param.type === "url" || param.type === "markdown" || param.type === "string" || param.type === "long_string" || param.type === "list") && !param.hidden) {
          list.push({ key: `param.${key}`, title: param.title })
        }
      })
    }
    if (field.includeContext) {
      list.push({ key: "context", title: "Context" })
    }
    if (field.includeContent) {
      list.push({ key: "full_content", title: "Content" })
    }

    return (
      <Select
        label={label}
        size={size}
        variant={inputVariant}
        selectedKeys={[value].filter(Boolean)}
        onSelectionChange={(keys) => {
          const next = Array.from(keys)[0]
          updateValue(next)
        }}
      >
        {list.map((option) => (
          <SelectItem key={option.key}>{option.title}</SelectItem>
        ))}
      </Select>
    )
  }

  if (field.type === "flag") {
    return (
      <div
        className={`w-full relative justify-end flex ${editable ? "ring-2 ring-indigo-500 ring-offset-2" : ""}`}
        onClick={editable ? () => updateValue(value ? undefined : true) : undefined}
      >
        {value
          ? <SolidFlagIcon className={`w-5 h-5 ${`text-${field.color}-400` || "text-red-400"}`} />
          : <FlagIcon className={`w-5 h-5 ${`text-${field.color}-400` || "text-red-400"}`} />
        }
      </div>
    )
  }

  if (field.type === "link") {
    return (
      <EditableResourceField
        onSelect={(next) => updateValue(next ? next : null)}
        value={value}
      />
    )
  }

  if (field.type === "internal_parent_pin") {
    let options = []
    let current = ""
    const page = primitive?.origin
    if (page) {
      const pins = page.inputPins
      options = Object.keys(pins).map((d) => ({ id: d, title: pins[d].name })).filter((d) => d.id !== "impin")
      const outputs = page.primitives.outputs
      current = Object.keys(outputs).find((d) => outputs[d].includes(primitive.id))?.split("_")?.[0]
    }
    return (
      <Select
        label={label}
        size={size}
        variant={inputVariant}
        selectedKeys={[current].filter(Boolean)}
        onSelectionChange={(keys) => {
          const next = Array.from(keys)[0]
          const outputs = page.primitives.outputs
          const existing = Object.keys(outputs).find((d) => outputs[d].includes(primitive.id))
          const newPath = `${next}_impin`
          if (existing !== newPath) {
            page.removeRelationship(primitive, `outputs.${existing}`)
            page.addRelationship(primitive, `outputs.${newPath}`)
          }
        }}
      >
        {options.map((option) => (
          <SelectItem key={option.id}>{option.title}</SelectItem>
        ))}
      </Select>
    )
  }

  if (field.type === "actionlist") {
    const actions = primitive?.referenceParameters?.referenceId ? mainstore.category(primitive?.referenceParameters?.referenceId).actions ?? [] : []
    return (
      <Select
        label={label}
        size={size}
        variant={inputVariant}
        selectedKeys={[value].filter(Boolean)}
        onSelectionChange={(keys) => {
          const next = Array.from(keys)[0]
          updateValue(next)
        }}
      >
        {actions.map((action) => (
          <SelectItem key={action.key}>{action?.title}</SelectItem>
        ))}
      </Select>
    )
  }

  if (field.type === "user") {
    const user = mainstore.user(value)
    const name = user?.name || value || field.default || "Unassigned"
    const inlineClass = mode === "inline" ? "truncate" : ""
    return (
      <div className={`flex place-items-center ${inlineClass} text-gray-500 font-medium`}>
        <UserIcon className="w-4 h-4 mr-1 text-slate-300" />
        {name}
      </div>
    )
  }

  const area = field.type === "long_string"
  return (
    <DebouncedInput
      area={area}
      label={label}
      variant={inputVariant}
      value={coerceString(value)}
      isDisabled={isDisabled}
      classNames={inputClassNames}
      onChange={(next) => updateValue(next)}
    />
  )
}
